import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/components/auth/ClerkAuthProvider';
import { useApiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormError } from '@/components/ui/form-error';
import { Building2, Loader2, Save, Upload, X, ImageIcon, MapPin, Link2, AlertCircle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  companySettingsSchema,
  type CompanySettingsFormData,
} from '@/lib/validations';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// API response type for tenant
interface TenantResponse {
  id: string;
  companyName: string;
  slug: string;
  brandingConfig?: {
    logo_url?: string;
    primary_color?: string;
  };
  widgetConfig?: {
    default_country?: 'US' | 'CA';
  };
}

export default function CompanySettings() {
  const { tenantId } = useAuthContext();
  const { getToken } = useAuth();
  const api = useApiClient();
  const queryClient = useQueryClient();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [companyCountry, setCompanyCountry] = useState<'US' | 'CA'>('US');
  const [widgetSlug, setWidgetSlug] = useState('');
  const [slugError, setSlugError] = useState<string | null>(null);

  // Fetch tenant from PostgreSQL API
  const { data: tenant, isLoading } = useQuery({
    queryKey: ['tenant-api', tenantId],
    queryFn: async (): Promise<TenantResponse | null> => {
      if (!tenantId) return null;
      return api.get<TenantResponse>(`/tenants/${tenantId}`);
    },
    enabled: !!tenantId,
  });

  // Update tenant mutation
  const updateTenant = useMutation({
    mutationFn: async (data: { companyName: string; slug?: string; brandingConfig?: object; widgetConfig?: object }) => {
      if (!tenantId) throw new Error('No tenant ID');
      const response = await api.patch(`/tenants/${tenantId}`, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-api', tenantId] });
    },
    onError: (error: Error) => {
      // Re-throw so handleSubmit can catch it
      throw error;
    },
  });

  const form = useForm<CompanySettingsFormData>({
    resolver: zodResolver(companySettingsSchema),
    defaultValues: {
      companyName: '',
      primaryColor: '#2563eb',
    },
  });

  // Update form when tenant data loads
  useEffect(() => {
    if (tenant) {
      form.reset({
        companyName: tenant.companyName || '',
        primaryColor: tenant.brandingConfig?.primary_color || '#2563eb',
      });
      if (tenant.brandingConfig?.logo_url) {
        setLogoPreview(tenant.brandingConfig.logo_url);
      }
      if (tenant.widgetConfig?.default_country) {
        setCompanyCountry(tenant.widgetConfig.default_country);
      }
      if (tenant.slug) {
        setWidgetSlug(tenant.slug);
      }
    }
  }, [tenant, form]);

  // Validate slug format
  const validateSlug = (slug: string): string | null => {
    if (slug.length < 3) {
      return 'URL must be at least 3 characters';
    }
    if (slug.length > 100) {
      return 'URL must be at most 100 characters';
    }
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
      return 'Use only lowercase letters, numbers, and hyphens';
    }
    return null;
  };

  // Handle slug input change
  const handleSlugChange = (value: string) => {
    // Auto-format: lowercase, replace spaces with hyphens, remove invalid chars
    const formatted = value
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-') // collapse multiple hyphens
      .replace(/^-|-$/g, ''); // remove leading/trailing hyphens

    setWidgetSlug(formatted);
    setSlugError(formatted ? validateSlug(formatted) : null);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo must be less than 2MB');
        return;
      }
      // Validate file type
      if (!['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'].includes(file.type)) {
        toast.error('Logo must be PNG, JPG, SVG, or WebP');
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  // Upload logo to R2 using presigned URL
  const uploadLogoToR2 = async (file: File): Promise<string> => {
    const token = await getToken();
    if (!token || !tenantId) throw new Error('Not authenticated');

    // Get presigned URL
    const presignedRes = await fetch(`${API_BASE_URL}/upload/presigned`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        tenantId,
        sessionId: 'branding', // Fixed session for branding assets
        fileName: `logo_${Date.now()}.${file.name.split('.').pop()}`,
        fileType: file.type,
      }),
    });

    if (!presignedRes.ok) {
      const error = await presignedRes.json();
      throw new Error(error.error || 'Failed to get upload URL');
    }

    const { data } = await presignedRes.json();
    const { uploadUrl, fileUrl } = data;

    // Upload file to R2
    const uploadRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!uploadRes.ok) {
      throw new Error('Failed to upload file');
    }

    return fileUrl;
  };

  const handleSubmit = async (data: CompanySettingsFormData) => {
    if (!tenantId) return;

    // Validate slug before submitting
    if (widgetSlug) {
      const error = validateSlug(widgetSlug);
      if (error) {
        setSlugError(error);
        toast.error(error);
        return;
      }
    }

    try {
      setIsUploading(true);
      setSlugError(null);

      // Upload logo if a new file was selected
      let logoUrl = tenant?.brandingConfig?.logo_url;
      if (logoFile) {
        try {
          logoUrl = await uploadLogoToR2(logoFile);
        } catch (err) {
          console.error('Logo upload failed:', err);
          toast.error('Failed to upload logo');
          setIsUploading(false);
          return;
        }
      } else if (logoPreview === null) {
        // Logo was removed
        logoUrl = undefined;
      }

      // Build update payload
      const brandingConfig: Record<string, string | undefined> = {
        primary_color: data.primaryColor || '#2563eb',
      };

      // Only include logo_url if it has a value
      if (logoUrl) {
        brandingConfig.logo_url = logoUrl;
      }

      // Build payload - only include slug if it changed
      const payload: Record<string, unknown> = {
        companyName: data.companyName,
        brandingConfig,
        widgetConfig: {
          default_country: companyCountry,
        },
      };

      // Only update slug if it changed
      if (widgetSlug && widgetSlug !== tenant?.slug) {
        payload.slug = widgetSlug;
      }

      await updateTenant.mutateAsync(payload);

      setLogoFile(null);
      toast.success('Company settings saved');
    } catch (err: unknown) {
      console.error('Failed to save settings:', err);
      // Check if it's a slug conflict error
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (errorMessage.includes('widget URL') || errorMessage.includes('slug')) {
        setSlugError('This widget URL is already taken');
        toast.error('This widget URL is already taken. Please choose a different one.');
      } else {
        toast.error('Failed to save settings');
      }
    } finally {
      setIsUploading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isSaving = updateTenant.isPending || isUploading;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Company Profile
        </CardTitle>
        <CardDescription>Update your company name and branding settings</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <div className="grid gap-6 sm:grid-cols-2">
            {/* Left column - Form fields */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  placeholder="Your Company Name"
                  {...form.register('companyName')}
                />
                <FormError message={form.formState.errors.companyName?.message} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="widgetSlug" className="flex items-center gap-2">
                  <Link2 className="h-4 w-4" />
                  Widget URL
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {window.location.origin}/widget/
                  </span>
                  <Input
                    id="widgetSlug"
                    value={widgetSlug}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    placeholder="your-company"
                    className={slugError ? 'border-destructive' : ''}
                  />
                </div>
                {slugError ? (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {slugError}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    This is your unique widget URL. Use lowercase letters, numbers, and hyphens.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Company Location
                </Label>
                <Select
                  value={companyCountry}
                  onValueChange={(val) => setCompanyCountry(val as 'US' | 'CA')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="US">🇺🇸 United States</SelectItem>
                    <SelectItem value="CA">🇨🇦 Canada</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  This determines the address format shown to customers in your widget
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="primaryColor">Brand Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="primaryColor"
                    type="color"
                    className="w-14 h-10 p-1 cursor-pointer"
                    {...form.register('primaryColor')}
                  />
                  <Input
                    placeholder="#2563eb"
                    {...form.register('primaryColor')}
                  />
                </div>
                <FormError message={form.formState.errors.primaryColor?.message} />
                <p className="text-xs text-muted-foreground">
                  This color is used for buttons and accents in your widget
                </p>
              </div>

              <Button
                type="submit"
                disabled={isSaving}
              >
                {isSaving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Company Settings
              </Button>
            </div>

            {/* Right column - Logo upload */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Company Logo</Label>
                <p className="text-xs text-muted-foreground">
                  Upload your logo to display in the widget header (max 2MB, PNG/JPG/SVG/WebP)
                </p>
              </div>

              <div className="flex items-start gap-4">
                {/* Logo preview */}
                <div className="w-32 h-20 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/50">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="max-w-full max-h-full object-contain p-2"
                    />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="logo-upload"
                    className="cursor-pointer inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {logoPreview ? 'Change' : 'Upload'}
                  </Label>
                  <Input
                    id="logo-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    className="hidden"
                    onChange={handleLogoChange}
                  />
                  {logoPreview && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleRemoveLogo}
                    >
                      <X className="mr-2 h-4 w-4" />
                      Remove
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Recommended: 200x60 pixels, transparent background
              </p>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
